import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Modal, PageContainer, Table, Tabs } from '@react-apps/design-system';
import type { TabItem } from '@react-apps/design-system';
import { useRouteTabs } from '@/shared/hooks/use-route-tabs';
import type { Invoice } from '../../domain/entities/sales.entity';
import { useBillingAgreement } from '../hooks/use-contract-details';
import { BillingAgreementPanel } from '../components/BillingAgreementPanel';
import {
  buildInvoiceColumns,
  FOCUSED_INVOICE_PARAM,
  InvoiceSummary,
} from '../shared/invoices';
import { ManualSettlementModal } from '../components/sales-manual-operation-modals';
import { InvoiceDuplicatePaymentSilence } from '../components/InvoiceDuplicatePaymentSilence';
import { EntityEventsSection } from '../components/EntityEventsSection';
import { ErrorState, SectionCard, SkeletonCard } from '../shared/display';

/**
 * Detalhe de uma Billing Agreement específica — inclusive uma já substituída por
 * renegociação, que a aba "Billing Agreement" do contrato (sempre a BA atual)
 * não alcança.
 *
 * Alcançada pelo modal "Ver acordos de pagamento" da página do contrato, e por
 * quem precisa apontar para uma **fatura**: fatura não tem rota própria, então
 * o link traz `?invoice=<id>` e a página abre o resumo dela já na chegada (ver
 * `FOCUSED_INVOICE_PARAM`). É o caminho que a Central de Pendências usa para
 * levar da pendência à fatura que a causou. O `?invoice=` vale na aba
 * Detalhes, que é a padrão, e por isso o link não precisa nomear a aba.
 */
const BILLING_AGREEMENT_TAB_IDS = ['details', 'audit'];

export function BillingAgreementDetailPage() {
  const { billingAgreementId } = useParams<{ billingAgreementId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedInvoiceId = searchParams.get(FOCUSED_INVOICE_PARAM);
  const id = billingAgreementId ?? '';
  const { activeTab, onTabChange } = useRouteTabs(
    `/billing-agreements/${id}`,
    BILLING_AGREEMENT_TAB_IDS,
  );
  const { data: detail, isLoading, isError } = useBillingAgreement(id);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [settlingInvoice, setSettlingInvoice] = useState<Invoice | null>(null);

  const ba = detail?.billingAgreement;

  /**
   * Abre o resumo da fatura apontada por `?invoice=` assim que as faturas
   * chegam. Id que não é desta BA não abre nada — a lista continua ali, e é
   * melhor que um modal vazio afirmando uma fatura que esta página não tem.
   */
  useEffect(() => {
    if (!focusedInvoiceId || !detail) return;
    const focused = detail.invoices.find(
      (invoice) => invoice.id === focusedInvoiceId,
    );
    if (focused) setSelectedInvoice(focused);
  }, [focusedInvoiceId, detail]);

  /**
   * Fechar o resumo tira o `?invoice=` da URL: sem isso, o próximo refetch
   * (qualquer ação invalida `['sales']`) traria um `detail` novo e o efeito
   * acima reabriria o modal que o operador acabou de fechar.
   */
  const closeInvoiceSummary = () => {
    setSelectedInvoice(null);
    if (!focusedInvoiceId) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete(FOCUSED_INVOICE_PARAM);
        return params;
      },
      { replace: true },
    );
  };

  const tabs: TabItem[] = [
    {
      id: 'details',
      label: 'Detalhes',
      content: isLoading ? (
        <SkeletonCard rows={6} />
      ) : isError || !detail || !ba ? (
        <ErrorState message="Billing Agreement não encontrada." />
      ) : (
        <div className="space-y-6">
          {/* Uma ação feita aqui também muda a página do contrato (que a BA só
            revela depois de carregar). Invalidar o namespace `sales` inteiro é
            mais simples e seguro que coordenar os dois prefixos, e o custo é
            desprezível na escala do admin. */}
          {/* Oferta e link do contrato vêm dentro do painel — dois cards de uma
            linha só cada não valiam o espaço. */}
          <BillingAgreementPanel
            billingAgreement={ba}
            offer={detail.offer}
            showContract
            linkId={false}
            invalidateKey={['sales']}
          />

          <SectionCard title="Faturas desta Billing Agreement">
            <Table
              columns={buildInvoiceColumns(
                setSelectedInvoice,
                setSettlingInvoice,
                // Escopo de uma BA só: o preço vigente estima os ciclos
                // placeholder em vez de deixar a coluna de valor cheia de traço.
                detail.valueSummary.perCycleValue,
              )}
              data={detail.invoices}
              keyExtractor={(row) => row.id}
              emptyMessage="Nenhuma invoice encontrada."
              striped
            />
          </SectionCard>
        </div>
      ),
    },
    {
      id: 'audit',
      label: 'Auditoria',
      content: (
        <EntityEventsSection entityType="BillingAgreement" entityId={id} />
      ),
    },
  ];

  return (
    <PageContainer maxWidth="4xl">
      <button
        type="button"
        onClick={() =>
          void navigate(ba ? `/contracts/${ba.contractId}` : '/contracts')
        }
        className="text-(--color-text-secondary) hover:text-(--color-text-primary) focus-visible:ring-(--color-accent) flex items-center gap-1.5 rounded text-sm transition-colors focus-visible:outline-none focus-visible:ring-2"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 19.5 8.25 12l7.5-7.5"
          />
        </svg>
        {ba ? 'Contrato' : 'Contratos'}
      </button>

      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />

      <Modal
        open={selectedInvoice !== null}
        onClose={closeInvoiceSummary}
        title="Resumo da Fatura"
        size="lg"
      >
        {selectedInvoice && (
          <div className="space-y-4">
            <InvoiceSummary invoice={selectedInvoice} />
            {/* `key`: trocar de fatura zera nota e desfecho da anterior. */}
            <InvoiceDuplicatePaymentSilence
              key={selectedInvoice.id}
              invoiceId={selectedInvoice.id}
            />
          </div>
        )}
      </Modal>

      {settlingInvoice && (
        <ManualSettlementModal
          open
          onClose={() => setSettlingInvoice(null)}
          invoice={settlingInvoice}
          invalidateKey={['sales']}
        />
      )}
    </PageContainer>
  );
}
